import React from 'react';

import { Button } from '@/components/shared/buttons/button';
import { ButtonTag } from '@/components/shared/buttons/button-tag';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Image } from '@/components/shared/media/image';
import { generateMobileAndLargeMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { MEDIA_QUERIES } from '@/components/shared/media/image/Image.constants';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { CSS } from '@/stitches.config';
import { IButton } from '@/types/shared';
import { ITest, ITracking } from '@/types/tracking';

interface CardProps {
  className?: string;
  css?: CSS;
  image: ImageProps;
  tag?: {
    label: string;
    url: string;
  };
  date?: string;
  title: string;
  description?: string;
  button?: IButton;
  linesTitle?: 2 | 3 | 4 | 5;
  linesDescription?: 2 | 3 | 4 | 5;
  variant?: 'gray-darkest' | 'white';
  test?: ITest;
  tracking?: ITracking;
}

export const Card: React.FC<CardProps> = React.memo(
  ({
    css,
    className,
    tag,
    image,
    title,
    description,
    date,
    button,
    linesTitle,
    linesDescription,
    tracking,
    test = { dataTest: 'card' },
    variant = 'gray-darkest',
    ...rest
  }) => {
    return (
      <Box
        css={css}
        className={className}
        display="grid"
        rowGap="6"
        test={{
          dataTest: test?.dataTest,
        }}
        {...rest}
      >
        {!!image?.src && (
          <>
            {button?.url ? (
              <Link
                href={button.url}
                target={button.target}
                tracking={{
                  dataTrack: tracking?.dataTrack as string,
                  dataTrackSection: tracking?.dataTrackSection,
                  dataTrackText: tracking?.dataTrackText || button.label,
                  dataTrackUrl: tracking?.dataTrackUrl || button.url,
                  dataTrackValue: tracking?.dataTrackValue || title,
                }}
                test={{
                  dataTest: test?.dataTest + '_link',
                }}
              >
                <Image
                  priority={image.priority}
                  rootMargin="10% 0px 0px 0px"
                  placeholder="shimmer"
                  hoverEffect="zoom"
                  src={image.src}
                  alt={image.alt}
                  width={image.width || 1600}
                  height={image.height || 900}
                  layout="responsive"
                  aspectRatio={image.aspectRatio || '16by9'}
                  sources={
                    image.sources || [
                      {
                        srcset: image.src,
                        media: { minWidth: 1024 },
                        options: { width: 640, crop: '16:9' },
                      },
                      {
                        srcset: image.src,
                        media: MEDIA_QUERIES.TABLET_MD,
                        options: { width: 576, crop: '16:9' },
                      },
                      ...generateMobileAndLargeMobileImageSources({
                        mobileSrc: image.src,
                        mobileOptions: {
                          crop: '16:9',
                        },
                      }),
                    ]
                  }
                />
              </Link>
            ) : (
              <Image
                priority={image.priority}
                src={image.src}
                alt={image.alt}
                width={image.width || 1600}
                height={image.height || 900}
                layout="responsive"
                aspectRatio={image.aspectRatio || '16by9'}
                sources={
                  image.sources || [
                    {
                      srcset: image.src,
                      media: { minWidth: 1024 },
                      options: { width: 640, crop: '16:9' },
                    },
                    {
                      srcset: image.src,
                      media: MEDIA_QUERIES.TABLET_MD,
                      options: { width: 576, crop: '16:9' },
                    },
                    ...generateMobileAndLargeMobileImageSources({
                      mobileSrc: image.src,
                      mobileOptions: {
                        crop: '16:9',
                      },
                    }),
                  ]
                }
              />
            )}
          </>
        )}
        <Box>
          {!!(tag || date) && (
            <Flex
              alignItems="center"
              gap="2"
              css={{ marginBottom: '$space-4' }}
            >
              {!!tag && (
                <ButtonTag
                  label={tag.label}
                  href={tag.url}
                  theme={variant === 'white' ? 'dark' : 'light'}
                />
              )}
              {!!date && (
                <Text
                  variant={variant === 'white' ? 'text-white' : 'text-gray'}
                  data-test={test?.dataTest + '_date'}
                >
                  {date}
                </Text>
              )}
            </Flex>
          )}
          {!!title && (
            <>
              {button?.url ? (
                <Link
                  href={button.url}
                  target={button.target}
                  hoverColor="red"
                  tracking={{
                    dataTrack: tracking?.dataTrack as string,
                    dataTrackSection: tracking?.dataTrackSection,
                    dataTrackText: tracking?.dataTrackText || button.label,
                    dataTrackUrl: tracking?.dataTrackUrl || button.url,
                    dataTrackValue: tracking?.dataTrackValue || title,
                  }}
                  test={{
                    dataTest: test?.dataTest + '_link',
                  }}
                >
                  <Text
                    as="h3"
                    size="text-sub-h2"
                    variant={
                      variant === 'white' ? 'text-white' : 'text-gray-darkest'
                    }
                    font="bold"
                    lines={linesTitle}
                    wrap
                    dangerouslySetInnerHTML={{
                      __html: title,
                    }}
                    data-test={test?.dataTest + '_title'}
                  />
                </Link>
              ) : (
                <Text
                  as="h3"
                  size="text-sub-h2"
                  variant={
                    variant === 'white' ? 'text-white' : 'text-gray-darkest'
                  }
                  font="bold"
                  lines={linesTitle}
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: title,
                  }}
                  data-test={test?.dataTest + '_title'}
                />
              )}
            </>
          )}
          {!!description && (
            <Text
              as="div"
              size="text-body-1"
              variant={variant === 'white' ? 'text-white' : 'text-gray-darkest'}
              lines={linesDescription}
              wrap
              dangerouslySetInnerHTML={{
                __html: description,
              }}
              css={{ marginTop: '$space-2' }}
              data-test={test?.dataTest + '_description'}
            />
          )}
          {!!button?.url && (
            <Button
              href={button.url}
              target={button.target}
              label={button.label}
              icon={button.icon}
              variant={variant === 'white' ? 'button-white' : 'button-gray'}
              size="md"
              border
              css={{
                marginTop: '$space-6',
              }}
              tracking={{
                dataTrack: tracking?.dataTrack as string,
                dataTrackSection: tracking?.dataTrackSection,
                dataTrackText: tracking?.dataTrackText || button.label,
                dataTrackUrl: tracking?.dataTrackUrl || button.url,
                dataTrackValue: tracking?.dataTrackValue || title,
              }}
              test={{
                dataTest: test?.dataTest + '_button',
              }}
            />
          )}
        </Box>
      </Box>
    );
  }
);

Card.displayName = 'Card';
